import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Calendar,
  User,
  Building2,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { format } from "date-fns";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import DashboardContainer from "@/components/layout/DashboardContainer";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  ClientContract,
  GetClientContractsResponse,
  ApproveClientContractResponse,
} from "@/types/client";

export default function ClientContractsContainer() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [contracts, setContracts] = useState<ClientContract[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<ClientContract | null>(null);
  const [isApproving, setIsApproving] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      setIsLoading(true);
      setFetchError(null);

      const response = await ApiService.request<GetClientContractsResponse>(
        EndpointKeys.GET_CLIENT_CONTRACTS,
        {
          method: "GET",
        },
      );

      if (response.status === "success") {
        setContracts(response.contracts);
      } else {
        throw new Error(response.error || "Failed to fetch client contracts");
      }
    } catch (error) {
      console.error("Error fetching client contracts:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Failed to load client contracts";
      setFetchError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (contractorAgreementId: string) => {
    try {
      // Generate download URL for the contractor agreement
      const response = await ApiService.request<{ download_url: string }>(
        EndpointKeys.DOWNLOAD_CONTRACTOR_AGREEMENT,
        {
          method: "GET",
          urlParams: { agreement_id: contractorAgreementId },
        },
      );

      if (response.download_url) {
        // Open the download URL in a new tab
        window.open(response.download_url, "_blank");
      } else {
        throw new Error("No download URL received");
      }
    } catch (error) {
      console.error("Error downloading agreement:", error);
      toast({
        title: "Error",
        description: "Failed to download agreement",
        variant: "destructive",
      });
    }
  };

  const handleApproveContract = (contract: ClientContract) => {
    setSelectedContract(contract);
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!selectedContract) return;

    try {
      setIsApproving(true);

      const response = await ApiService.request<ApproveClientContractResponse>(
        EndpointKeys.APPROVE_CLIENT_CONTRACT,
        {
          method: "POST",
          urlParams: { contractId: selectedContract.id },
        },
      );

      if (response.status === "success") {
        toast({
          title: "Success",
          description: "Contract approved and signed successfully",
        });

        setShowApprovalModal(false);
        setSelectedContract(null);

        // Refresh contracts to show updated status
        fetchContracts();
      } else {
        throw new Error(response.error || "Failed to approve contract");
      }
    } catch (error) {
      console.error("Error approving contract:", error);
      toast({
        title: "Error",
        description: "Failed to approve contract",
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "default";
      case "PENDING_CLIENT_APPROVAL":
        return "secondary";
      case "DRAFT":
        return "outline";
      case "TERMINATED":
      case "CANCELLED":
        return "destructive";
      case "COMPLETED":
        return "default";
      case "SUSPENDED":
        return "secondary";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <CheckCircle className="h-4 w-4" />;
      case "PENDING_CLIENT_APPROVAL":
        return <Clock className="h-4 w-4" />;
      case "TERMINATED":
      case "CANCELLED":
        return <XCircle className="h-4 w-4" />;
      case "COMPLETED":
        return <CheckCircle className="h-4 w-4" />;
      case "SUSPENDED":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const needsApproval = (contract: ClientContract) => {
    return (
      contract.status === "PENDING_CLIENT_APPROVAL" && !contract.clientSignedAt
    );
  };

  const isActive = (contract: ClientContract) => {
    return contract.status === "ACTIVE";
  };

  const isCompleted = (contract: ClientContract) => {
    return contract.status === "COMPLETED";
  };

  return (
    <>
      <DashboardContainer
        title="My Contracts"
        headerContent={
          <div className="text-sm text-muted-foreground">
            Review and manage your contractor agreements
          </div>
        }
      >
        <div className="space-y-6">
          {fetchError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{fetchError}</AlertDescription>
            </Alert>
          )}

          {/* Summary Cards */}
          {!isLoading && contracts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="text-sm font-medium">Needs Approval</p>
                      <p className="text-2xl">
                        {contracts.filter(needsApproval).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium">Active</p>
                      <p className="text-2xl">
                        {contracts.filter(isActive).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium">Total</p>
                      <p className="text-2xl">{contracts.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-6 w-20" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : contracts.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Contracts Found</h3>
                <p className="text-muted-foreground text-center mb-6">
                  You don't have any contractor agreements yet. Once a
                  contractor sends you an invitation, you'll see their contracts
                  here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {contracts.map((contract) => (
                <Card key={contract.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          {contract.contractTitle || "Untitled Contract"}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Calendar className="h-4 w-4" />
                          Created{" "}
                          {format(new Date(contract.createdAt), "dd/MM/yyyy")}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={getStatusBadgeVariant(contract.status)}
                          className="gap-1"
                        >
                          {getStatusIcon(contract.status)}
                          {formatStatus(contract.status)}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Client</p>
                          <p className="text-sm text-muted-foreground">
                            {contract.contractTerms.client_full_legal_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Contractor</p>
                          <p className="text-sm text-muted-foreground">
                            {contract.contractTerms.contractor_full_legal_name}
                            {contract.contractTerms
                              .contractor_business_name && (
                              <span className="block text-xs">
                                (
                                {
                                  contract.contractTerms
                                    .contractor_business_name
                                }
                                )
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-sm font-medium">Fee Rate</p>
                          <p className="text-sm text-muted-foreground">
                            {contract.contractTerms.fee_currency_symbol}
                            {contract.contractTerms.fee_rate} per{" "}
                            {contract.contractTerms.fee_basis}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Contract Description */}
                    {contract.contractDescription && (
                      <div className="mb-4">
                        <p className="text-sm text-muted-foreground">
                          {contract.contractDescription}
                        </p>
                      </div>
                    )}

                    {/* Signature Status */}
                    <div className="border-t pt-4 mb-4">
                      <h4 className="font-medium mb-2">Signature Status</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-2 w-2 rounded-full ${contract.contractorSignedAt ? "bg-green-500" : "bg-gray-300"}`}
                          />
                          <span className="text-sm">
                            Contractor:{" "}
                            {contract.contractorSignedAt
                              ? `Signed ${formatDate(contract.contractorSignedAt)}`
                              : "Not signed"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-2 w-2 rounded-full ${contract.clientSignedAt ? "bg-green-500" : "bg-gray-300"}`}
                          />
                          <span className="text-sm">
                            Client:{" "}
                            {contract.clientSignedAt
                              ? `Signed ${formatDate(contract.clientSignedAt)}`
                              : "Not signed"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="text-sm text-muted-foreground">
                        Start Date:{" "}
                        {contract.contractTerms.start_date
                          ? format(
                              new Date(
                                contract.contractTerms.start_date + "T00:00:00",
                              ),
                              "dd/MM/yyyy",
                            )
                          : "Not specified"}
                      </div>
                      <div className="flex gap-2">
                        {needsApproval(contract) && (
                          <Button
                            onClick={() => handleApproveContract(contract)}
                            className="gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Review & Approve
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDownload(contract.contractorAgreementId)
                          }
                          className="gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DashboardContainer>

      {/* Contract Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Contract</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve and sign this contract? This
              action cannot be undone. Once approved, the contract will become
              active and legally binding.
            </DialogDescription>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-muted/50">
                <h4 className="font-medium mb-2">
                  {selectedContract.contractTitle}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium">Contractor:</span>
                    <br />
                    {selectedContract.contractTerms.contractor_full_legal_name}
                  </div>
                  <div>
                    <span className="font-medium">Fee Rate:</span>
                    <br />
                    {selectedContract.contractTerms.fee_currency_symbol}
                    {selectedContract.contractTerms.fee_rate} per{" "}
                    {selectedContract.contractTerms.fee_basis}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApprovalModal(false)}
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button onClick={handleApprovalSubmit} disabled={isApproving}>
              {isApproving ? "Approving..." : "Approve & Sign Contract"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
